// SPDX-License-Identifier: AGPL-3.0-or-later

import type {CosmeticType, KnownCosmeticType, ROOM_CHARACTER_TYPE} from '@app/features/cosmetics/state/Cosmetics';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';

export const COSMETIC_TYPE_LABELS: Record<KnownCosmeticType, MessageDescriptor> = {
	avatar_decoration: msg({
		message: 'Avatar decoration',
		comment: 'A frame drawn around the user avatar. Profile decorations settings section title and search entry.',
	}),
	avatar_ultra: msg({
		message: 'Ultra decoration',
		comment: 'Big decoration, such as wings, behind the avatar. Profile decorations section title and search entry.',
	}),
	nameplate: msg({
		message: 'Nameplate',
		comment:
			'A background image behind the user name in member and direct message lists. Profile decorations settings section title and search entry.',
	}),
	profile_effect: msg({
		message: 'Profile effect',
		comment:
			'An animation played over the user profile card. Profile decorations settings section title and search entry.',
	}),
	room_character: msg({
		message: 'Room character',
		comment:
			'The 3D character that stands for the user in 3D voice rooms. Profile decorations settings section title and shop item type.',
	}),
	room_face: msg({
		message: 'Face',
		context: 'room-character-slot',
		comment:
			'The face (expression) of a 3D room character. Character editor slot tab, shop row title and shop item type.',
	}),
	room_hair: msg({
		message: 'Hair',
		context: 'room-character-slot',
		comment: 'The hairstyle of a 3D room character. Character editor slot tab, shop row title and shop item type.',
	}),
	room_top: msg({
		message: 'Top',
		context: 'room-character-slot',
		comment:
			'The shirt or jacket of a 3D room character. Character editor slot tab, shop row title and shop item type.',
	}),
	room_bottom: msg({
		message: 'Bottom',
		context: 'room-character-slot',
		comment:
			'The trousers or shorts of a 3D room character. Character editor slot tab, shop row title and shop item type.',
	}),
	room_shoes: msg({
		message: 'Shoes',
		context: 'room-character-slot',
		comment: 'The shoes or socks of a 3D room character. Character editor slot tab, shop row title and shop item type.',
	}),
	room_costume: msg({
		message: 'Costume',
		context: 'room-character-slot',
		comment:
			'A one-piece outfit of a 3D room character that replaces the top and the bottom. Character editor slot tab, shop row title and shop item type.',
	}),
	room_hat: msg({
		message: 'Hat',
		context: 'room-character-slot',
		comment: 'The hat of a 3D room character. Character editor slot tab, shop row title and shop item type.',
	}),
	room_glasses: msg({
		message: 'Glasses',
		context: 'room-character-slot',
		comment: 'The glasses of a 3D room character. Character editor slot tab, shop row title and shop item type.',
	}),
	room_accessory: msg({
		message: 'Accessory',
		context: 'room-character-slot',
		comment:
			'A small extra on a 3D room character, such as headphones or a moustache. Character editor slot tab, shop row title and shop item type.',
	}),
	room_emote: msg({
		message: 'Room emote',
		comment:
			'A body movement, such as a dance, that the 3D room character plays from the emote menu in 3D voice rooms. Shop row title and shop item type.',
	}),
};

export const COSMETIC_TYPE_DESCRIPTIONS: Record<CosmeticType | typeof ROOM_CHARACTER_TYPE, MessageDescriptor> = {
	avatar_decoration: msg({
		message: 'A frame around your avatar.',
		comment: 'Profile decorations settings: what an avatar decoration is. Also the settings search entry description.',
	}),
	avatar_ultra: msg({
		message: 'Wings and other large decorations behind your avatar.',
		comment: 'Profile decorations settings: what ultra decorations are. Also the settings search entry description.',
	}),
	nameplate: msg({
		message: 'A background behind your name in member lists and direct messages.',
		comment: 'Profile decorations settings: what a nameplate is. Also the settings search entry description.',
	}),
	profile_effect: msg({
		message: 'An animation that plays over your profile card.',
		comment: 'Profile decorations settings: what a profile effect is. Also the settings search entry description.',
	}),
	room_character: msg({
		message: 'Your character in 3D voice rooms.',
		comment: 'Profile decorations settings: what a room character is.',
	}),
};
